// @flow
// Sheets that leave the way they came, in the phone layout: a pick list or the date picker slides down while its
// backdrop fades, a full-screen form sheet slides down, any other modal fades out. React has already decided to
// remove the element, so the exit plays on a copy taken just before it goes (Modal's componentWillUnmount,
// CustomDayPicker closing): nothing about who closes what changes, and nothing is mounted twice. The copy shows
// what the tap that closed it picked, keeps typed values, scroll offsets and drawn canvases, and goes as soon as
// another sheet opens.
// Desktop, reduced motion, modals marked .noAnimation and browsers without the Web Animations API keep the
// instant close.

// The curve is the CSS's (--motion-curve, AppLayout.scss: iOS's in the iPhone app, Material's otherwise). Exits
// are a little quicker than entries.
const EXIT = { ios: 250, android: 200 };
const FADE_EXIT = 150;
const FALLBACK_CURVE = 'cubic-bezier(.2, 0, 0, 1)';
// Under 700px a form sheet fills the screen (Modal.scss); wider (an iPad in portrait) it's a centered dialog
const FULL_SCREEN_SHEET = 700;

export const motionTiming = () => {
  const html = document.documentElement;
  const curve = (html && window.getComputedStyle(html).getPropertyValue('--motion-curve').trim()) || FALLBACK_CURVE;
  const ios = Boolean(html && html.classList.contains('iosApp'));
  return { curve, exit: ios ? EXIT.ios : EXIT.android };
};

export const prefersReducedMotion = () => (
  typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
);

// The phone layout (checkIfIsMobile): the apps under 1020px
const isPhoneLayout = () => Boolean(window.isCordovaApp) && window.innerWidth < 1020;

type ExitPlan = { kind: 'sheet', panel: string, backdrop: string } | { kind: 'formSheet' } | { kind: 'fade' };

// The bottom sheets (R1): the panel that slides and the backdrop that fades, as selectors inside the root
export const SHEETS = {
  list: { kind: 'sheet', panel: '.modalLayout', backdrop: '.modalOverlay' },
  dayPicker: { kind: 'sheet', panel: '.customDayPickerWrapper', backdrop: '.dayPickerBackdrop' },
};

// What leaves, and how. `node` is a Modal's root (.modal) or the date picker's overlay.
export const exitPlan = (node: HTMLElement): ?ExitPlan => {
  const has = (c) => node.classList.contains(c);
  if (has('noAnimation')) return null;
  if (has('dayPickerOverlay')) return has('isMobile') ? SHEETS.dayPicker : null;
  if (!has('modal')) return null;
  if (has('modalClickableList') && has('isMobile')) return SHEETS.list;
  // The full-screen form sheets (Modal.scss: #mobileApp .modal.asView, full screen under 700px)
  if (has('asView') && node.closest('#mobileApp') && window.innerWidth < FULL_SCREEN_SHEET) return { kind: 'formSheet' };
  return { kind: 'fade' };
};

const running: Set<{ cancel: Function }> = new Set();

// Drops every exit still playing: a sheet that opens never shares the page with a copy (ids, DOM queries)
export const finishExits = () => {
  Array.from(running).forEach((exit) => exit.cancel());
};

// A sheet that replaces another in the same update (the receipt's loading sheet → its form, a pick list → the
// form or the date picker it opens) shows at once: the one leaving doesn't slide out underneath it. Modal's
// constructor and the date picker's opening run before the old one's componentWillUnmount, so a flag set there
// and cleared after the update tells them apart.
let openingNow = false;
export const noteModalOpening = () => {
  finishExits();
  if (openingNow) return;
  openingNow = true;
  Promise.resolve().then(() => { openingNow = false; });
};

// The tap that closed a sheet (a row, a day) is what its copy shows as picked: the copy is taken from the render
// before the pick
let lastTap = { target: null, at: 0 };
const PICKS = [['.listItem', 'active'], ['.DayPicker-Day', 'DayPicker-Day--highlighted']];
if (typeof document !== 'undefined') {
  document.addEventListener('click', (e: any) => { lastTap = { target: e.target, at: Date.now() }; }, true);
}

const showPick = (from: HTMLElement, to: HTMLElement) => {
  const { target }: any = lastTap;
  if (!target || Date.now() - lastTap.at > 600 || !from.contains(target) || typeof target.closest !== 'function') return;
  PICKS.forEach(([selector, picked]) => {
    const row = target.closest(selector);
    if (!row || row.classList.contains('headingSeparator')) return;
    const index = Array.from(from.querySelectorAll(selector)).indexOf(row);
    const rows = to.querySelectorAll(selector);
    if (index < 0 || !rows[index]) return;
    rows.forEach((r) => r.classList.remove(picked));
    rows[index].classList.add(picked);
  });
};

// The copy loses what isn't an attribute: typed values, checks, drawn canvases, scroll offsets
const copyLiveState = (from: HTMLElement, to: HTMLElement) => {
  const src = from.querySelectorAll('input, textarea, select');
  const dst = to.querySelectorAll('input, textarea, select');
  src.forEach((el: any, i) => {
    const copy: any = dst[i];
    if (!copy) return;
    if (el.type === 'checkbox' || el.type === 'radio') copy.checked = el.checked;
    else if (el.type !== 'file') copy.value = el.value;
  });
  const canvases = to.querySelectorAll('canvas');
  from.querySelectorAll('canvas').forEach((el: any, i) => {
    const copy: any = canvases[i];
    try {
      if (copy && el.width && el.height) copy.getContext('2d').drawImage(el, 0, 0);
    } catch (e) { /* a canvas it can't read: left blank */ }
  });
};

// Only the containers that scroll in these sheets: not a walk of the whole tree
const SCROLLERS = '.modalContent, .modalMiddle, .clickableList, .modalGrid, .modalLayout, .form, .customDayPickerWrapper';
const scrolledOffsets = (root: HTMLElement) => Array.from(root.querySelectorAll(SCROLLERS))
  .map((el, i) => [i, el.scrollTop, el.scrollLeft])
  .filter(([, top, left]) => top > 0 || left > 0);

const startTransform = (el: HTMLElement) => {
  const t = window.getComputedStyle(el).transform;
  return t && t !== 'none' ? t : 'translate3d(0, 0, 0)';
};

const startOpacity = (el: ?HTMLElement) => {
  if (!el) return 1;
  const o = parseFloat(window.getComputedStyle(el).opacity);
  return Number.isNaN(o) ? 1 : o;
};

const play = (node: HTMLElement) => {
  if (!node || !node.parentNode || !node.isConnected) return null;
  if (typeof node.animate !== 'function' || !isPhoneLayout() || prefersReducedMotion() || openingNow) return null;
  if (node.querySelector('video')) return null; // a live camera can't be copied: close at once
  const plan = exitPlan(node);
  if (!plan) return null;

  const { curve, exit } = motionTiming();
  // Where it is now: mid-entry, or wherever a drag left it
  const panelNow = plan.kind === 'sheet' ? node.querySelector(plan.panel) : node;
  const from = panelNow ? startTransform(panelNow) : null;
  const backdropFrom = plan.kind === 'sheet' ? startOpacity(node.querySelector(plan.backdrop)) : 1;
  const opacityFrom = startOpacity(node);
  const offsets = scrolledOffsets(node);

  const copy: any = node.cloneNode(true);
  copy.classList.add('motionExit'); // AppLayout.scss: no entry animations, no taps
  copy.setAttribute('aria-hidden', 'true');
  copy.inert = true;
  copyLiveState(node, copy);
  showPick(node, copy);
  node.parentNode.insertBefore(copy, node.nextSibling);
  if (offsets.length) {
    const scrollers = copy.querySelectorAll(SCROLLERS);
    offsets.forEach(([i, top, left]) => {
      if (!scrollers[i]) return;
      scrollers[i].scrollTop = top;
      scrollers[i].scrollLeft = left;
    });
  }

  const animations = [];
  const options = { duration: exit, easing: curve, fill: 'forwards' };
  if (plan.kind === 'sheet') {
    const panel = copy.querySelector(plan.panel);
    const backdrop = copy.querySelector(plan.backdrop);
    if (panel && from) animations.push(panel.animate([{ transform: from }, { transform: 'translate3d(0, 100%, 0)' }], options));
    if (backdrop) animations.push(backdrop.animate([{ opacity: backdropFrom }, { opacity: 0 }], options));
  } else if (plan.kind === 'formSheet') {
    animations.push(copy.animate([{ transform: from }, { transform: 'translate3d(0, 100%, 0)' }], options));
  } else {
    animations.push(copy.animate([{ opacity: opacityFrom }, { opacity: 0 }], { duration: FADE_EXIT, easing: 'linear', fill: 'forwards' }));
  }

  let timer;
  const entry = {
    cancel: () => {
      clearTimeout(timer);
      running.delete(entry);
      animations.forEach((a) => { try { a.cancel(); } catch (e) { /* already gone */ } });
      copy.remove();
    },
  };
  running.add(entry);
  // The copy goes when its animation ends, or a little later at the latest (a paused or hidden page)
  const longest = plan.kind === 'fade' ? FADE_EXIT : exit;
  timer = setTimeout(entry.cancel, longest + 400);
  if (animations[0] && animations[0].finished) animations[0].finished.then(entry.cancel, () => {});
  return copy;
};

// Plays the exit of `node`, which is about to leave the page. Returns the copy, or null when nothing plays. Never
// throws: it runs while React commits, and a closing modal must close whatever the animation does.
export const playExit = (node: ?HTMLElement) => {
  try {
    return node ? play(node) : null;
  } catch (e) {
    return null;
  }
};
